import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { Resource, Project, ResourceAllocation, Holiday, ResourceRoleType, Customer, AllocationInterval, TimeOffRequest } from '../../types'
import { dateCalculationService } from '../../services/dateCalculationService'
import { allocationDistributionService } from '../../services/allocationDistributionService'
import { currencyService } from '../../services/currencyService'
import { dataService } from '../../services/dataService'
import { ArrowLeft, ArrowRight } from '../icons/Icons'
import { Modal } from '../ui/Modal'
import g from './GanttChart.module.css'

type ViewType = 'day' | 'week' | 'month' | 'quarter' | 'year'
type GanttMode = 'resource' | 'project'
type DragMode = 'move' | 'resize-start' | 'resize-end' | 'split' | null
type InteractionMode = 'none' | 'creating' | 'dragging' | 'split'

interface Props {
  resources: Resource[]
  projects: Project[]
  allocations: ResourceAllocation[]
  holidays: Holiday[]
  timeOffRequests: TimeOffRequest[]
  resourceRoleTypes: ResourceRoleType[]
  customers: Customer[]
  orgId: string
  onRefresh: () => Promise<void>
}

const BAR_HEIGHT = 20
const BAR_GAP = 4
const BAR_TOP_PAD = 8

const COLORS = {
  overbooked: '#2D55A5',
  full: '#107c10',
  partial: '#ff7b00',
  low: '#d13438',
  nonBillable: '#8a8886',
}

function parseDateUTC(s: string) {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

function dateStr(d: Date) {
  return d.toISOString().split('T')[0]
}

function getBarColor(billable: boolean, dailyTotal: number) {
  if (!billable) return COLORS.nonBillable
  if (dailyTotal > 8) return COLORS.overbooked
  if (dailyTotal === 8) return COLORS.full
  if (dailyTotal >= 3) return COLORS.partial
  return COLORS.low
}

function getAllocationRows(allocs: ResourceAllocation[]): ResourceAllocation[][] {
  const rows: ResourceAllocation[][] = []
  allocs.forEach(alloc => {
    let placed = false
    for (const row of rows) {
      const overlaps = row.some(r =>
        parseDateUTC(r.allocation_start) <= parseDateUTC(alloc.allocation_end) &&
        parseDateUTC(r.allocation_end) >= parseDateUTC(alloc.allocation_start)
      )
      if (!overlaps) { row.push(alloc); placed = true; break }
    }
    if (!placed) rows.push([alloc])
  })
  return rows
}

function truncate(text: string, max: number) {
  return text.length > max ? text.slice(0, max) + '…' : text
}

export default function GanttChart({ resources, projects, allocations, holidays, timeOffRequests, resourceRoleTypes, customers, orgId, onRefresh }: Props) {
  const [viewType, setViewType] = useState<ViewType>('month')
  const [ganttMode, setGanttMode] = useState<GanttMode>('resource')
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    const now = new Date()
    return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
  })
  const [tooltip, setTooltip] = useState<{ alloc: ResourceAllocation; x: number; y: number } | null>(null)
  const [selectedAlloc, setSelectedAlloc] = useState<ResourceAllocation | null>(null)
  const [interactionMode, setInteractionMode] = useState<InteractionMode>('none')
  const [dragMode, setDragMode] = useState<DragMode>(null)
  const [dragStart, setDragStart] = useState<{ x: number; y: number; alloc: ResourceAllocation | null; resourceId?: string } | null>(null)
  const [dragPreview, setDragPreview] = useState<{ start: string; end: string; left: number; width: number } | null>(null)
  const [contextMenu, setContextMenu] = useState<{ alloc: ResourceAllocation; x: number; y: number } | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showSplitModal, setShowSplitModal] = useState(false)
  const [splitDate, setSplitDate] = useState('')
  const [createForm, setCreateForm] = useState({
    project_id: '', resource_id: '', role_type_id: '', allocation_start: '', allocation_end: '',
    daily_hours: 8, billable: true, allocation_interval: 'Daily' as AllocationInterval
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = 0
  }, [viewType, currentDate, ganttMode])

  const getVisibleRange = useCallback((): { start: Date; end: Date } => {
    const base = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate()))
    switch (viewType) {
      case 'day': return { start: base, end: new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate(), 23, 59, 59, 999)) }
      case 'week': return { start: dateCalculationService.getStartOfWeek(base), end: dateCalculationService.getEndOfWeek(base) }
      case 'month': return { start: dateCalculationService.getStartOfMonth(base), end: dateCalculationService.getEndOfMonth(base) }
      case 'quarter': return { start: dateCalculationService.getStartOfQuarter(base), end: dateCalculationService.getEndOfQuarter(base) }
      case 'year': return { start: dateCalculationService.getStartOfYear(base), end: dateCalculationService.getEndOfYear(base) }
    }
  }, [viewType, currentDate])

  const { start: visStart, end: visEnd } = getVisibleRange()

  const navigate = (dir: -1 | 1) => {
    const d = new Date(currentDate)
    switch (viewType) {
      case 'day': d.setUTCDate(d.getUTCDate() + dir); break
      case 'week': d.setUTCDate(d.getUTCDate() + 7 * dir); break
      case 'month': d.setUTCMonth(d.getUTCMonth() + dir); break
      case 'quarter': d.setUTCMonth(d.getUTCMonth() + 3 * dir); break
      case 'year': d.setUTCFullYear(d.getUTCFullYear() + dir); break
    }
    setCurrentDate(d)
  }

  const goToday = () => {
    const now = new Date()
    setCurrentDate(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())))
  }

  const COL_WIDTH = useMemo(() => {
    if (viewType === 'day') return 0
    if (viewType === 'week') return 96
    if (viewType === 'month') return 97
    if (viewType === 'quarter') return 224
    return 80
  }, [viewType])

  const headers = useMemo(() => {
    const list: { label: string; date: Date; isWeekend: boolean; isHoliday: boolean; colSpan?: number }[] = []

    if (viewType === 'day' || viewType === 'week' || viewType === 'month') {
      const daysCount = viewType === 'day' ? 1 : viewType === 'week' ? 7
        : new Date(Date.UTC(visStart.getUTCFullYear(), visStart.getUTCMonth() + 1, 0)).getUTCDate()
      let cur = new Date(visStart)
      for (let i = 0; i < daysCount; i++) {
        list.push({
          label: cur.toLocaleDateString('en-US', { day: 'numeric', month: 'short', timeZone: 'UTC' }),
          date: new Date(cur),
          isWeekend: dateCalculationService.isWeekend(cur),
          isHoliday: dateCalculationService.isHoliday(cur, holidays),
        })
        cur = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth(), cur.getUTCDate() + 1))
      }
    } else if (viewType === 'quarter') {
      let cur = new Date(visStart)
      for (let i = 0; i < 3; i++) {
        list.push({
          label: new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth(), 1))
            .toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }),
          date: new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth(), 1)),
          isWeekend: false,
          isHoliday: false,
        })
        cur = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 1, 1))
      }
    } else {
      for (let i = 0; i < 12; i++) {
        const d = new Date(Date.UTC(visStart.getUTCFullYear(), i, 1))
        list.push({
          label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }),
          date: d,
          isWeekend: false,
          isHoliday: false,
        })
      }
    }
    return list
  }, [viewType, visStart, holidays])

  const isAutoWidth = viewType === 'day'
  const totalCols = headers.length

  const isNonWorking = (d: Date) => dateCalculationService.isWeekend(d) || dateCalculationService.isHoliday(d, holidays)

  const resourceTimeOffMap = useMemo(() => {
    const map: Record<string, Set<string>> = {}
    resources.forEach(r => {
      map[r.id] = dateCalculationService.getResourceTimeOffDateSet(r.id, timeOffRequests)
    })
    return map
  }, [resources, timeOffRequests])

  const dailyHoursMap = useMemo(() => {
    const map: Record<string, Record<string, number>> = {}
    allocations.forEach(alloc => {
      if (!alloc.billable) return
      const dailyHours = allocationDistributionService.calculateDailyHoursFromAllocation(
        alloc.allocation_start,
        alloc.allocation_end,
        alloc.daily_hours,
        alloc.allocation_interval,
        holidays
      )
      if (!map[alloc.resource_id]) map[alloc.resource_id] = {}
      Object.entries(dailyHours).forEach(([date, hours]) => {
        map[alloc.resource_id][date] = (map[alloc.resource_id][date] || 0) + hours
      })
    })
    return map
  }, [allocations, holidays])

  // Add Per Allocation Hours Map to display proper hours per day on Gantt Chart
    const perAllocationHoursMap = useMemo(() => {
    const map: Record<string, Record<string, number>> = {}
    allocations.forEach(alloc => {
      const dailyHours = allocationDistributionService.calculateDailyHoursFromAllocation(
        alloc.allocation_start,
        alloc.allocation_end,
        alloc.daily_hours,
        alloc.allocation_interval,
        holidays
      )
      map[alloc.id] = dailyHours
    })
    return map
  }, [allocations, holidays])


  const calcBarPos = useCallback((alloc: ResourceAllocation): { left: number; width: number; visible: boolean } => {
    const allocStart = parseDateUTC(alloc.allocation_start)
    const allocEndRaw = parseDateUTC(alloc.allocation_end)
    const allocEnd = viewType === 'day' ? allocEndRaw : new Date(Date.UTC(allocEndRaw.getUTCFullYear(), allocEndRaw.getUTCMonth(), allocEndRaw.getUTCDate() + 1))

    if (allocEnd < visStart || allocStart > visEnd) return { left: 0, width: 0, visible: false }
    if (viewType === 'day') return { left: 0, width: 0, visible: true }

    const dispStart = new Date(Math.max(allocStart.getTime(), visStart.getTime()))
    const dispEnd = new Date(Math.min(allocEnd.getTime(), visEnd.getTime()))

    if (viewType === 'quarter' || viewType === 'year') {
      const chartStartMo = visStart.getUTCFullYear() * 12 + visStart.getUTCMonth()
      const sMo = dispStart.getUTCFullYear() * 12 + dispStart.getUTCMonth()
      const eMo = dispEnd.getUTCFullYear() * 12 + dispEnd.getUTCMonth()

      const sMoStart = new Date(Date.UTC(dispStart.getUTCFullYear(), dispStart.getUTCMonth(), 1))
      const sMoEnd = new Date(Date.UTC(dispStart.getUTCFullYear(), dispStart.getUTCMonth() + 1, 1))
      const sOff = (dispStart.getTime() - sMoStart.getTime()) / (sMoEnd.getTime() - sMoStart.getTime())

      const eMoStart = new Date(Date.UTC(dispEnd.getUTCFullYear(), dispEnd.getUTCMonth(), 1))
      const eMoEnd = new Date(Date.UTC(dispEnd.getUTCFullYear(), dispEnd.getUTCMonth() + 1, 1))
      const eOff = Math.min(1, Math.max(0, (dispEnd.getTime() - eMoStart.getTime()) / (eMoEnd.getTime() - eMoStart.getTime())))

      const startIdx = (sMo - chartStartMo) + sOff
      const endIdx = (eMo - chartStartMo) + eOff
      if (startIdx >= endIdx) return { left: 0, width: 0, visible: false }
      return { left: startIdx * COL_WIDTH, width: (endIdx - startIdx) * COL_WIDTH, visible: true }
    }

    const dayMs = 86400000
    const startIdx = Math.floor((dispStart.getTime() - visStart.getTime()) / dayMs)
    const endIdx = Math.ceil((dispEnd.getTime() - visStart.getTime()) / dayMs)
    if (startIdx >= endIdx) return { left: 0, width: 0, visible: false }
    return { left: startIdx * COL_WIDTH, width: (endIdx - startIdx) * COL_WIDTH, visible: true }
  }, [viewType, visStart, visEnd, COL_WIDTH])

  const todayStr = dateStr(new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())))

  const handleMouseMove = useCallback((e: React.MouseEvent, alloc: ResourceAllocation) => {
    if (interactionMode !== 'none') return
    const TOOLTIP_W = 300, TOOLTIP_H = 240, OFF = 14
    let x = e.clientX + OFF, y = e.clientY + OFF
    if (x + TOOLTIP_W > window.innerWidth) x = e.clientX - TOOLTIP_W - OFF
    if (y + TOOLTIP_H > window.innerHeight) y = e.clientY - TOOLTIP_H - OFF
    setTooltip({ alloc, x, y })
  }, [interactionMode])

  const handleMouseLeave = useCallback(() => {
    if (interactionMode === 'none') setTooltip(null)
  }, [interactionMode])

  const getDateFromPosition = useCallback((x: number): Date | null => {
  if (!timelineRef.current || viewType === 'day') return null
  const rect = timelineRef.current.getBoundingClientRect()
  const scrollLeft = scrollRef.current?.scrollLeft || 0
  const relX = x - rect.left + scrollLeft
  const colIndex = Math.floor(relX / COL_WIDTH)
  if (colIndex < 0 || colIndex >= headers.length) return null
  return headers[colIndex].date
}, [COL_WIDTH, headers, viewType])


  const handleBarMouseDown = useCallback((e: React.MouseEvent, alloc: ResourceAllocation) => {
    if (e.button !== 0) return
    e.stopPropagation()

    const target = e.currentTarget as HTMLElement
    const rect = target.getBoundingClientRect()
    const relX = e.clientX - rect.left
    const width = rect.width

    let mode: DragMode = 'move'
    if (relX < width * 0.15) mode = 'resize-start'
    else if (relX > width * 0.85) mode = 'resize-end'
    else if (e.altKey) mode = 'split'

    setDragMode(mode)
    setDragStart({ x: e.clientX, y: e.clientY, alloc })
    setInteractionMode(mode === 'split' ? 'split' : 'dragging')
    setSelectedAlloc(alloc)
    setTooltip(null)
  }, [])

  const handleTimelineMouseDown = useCallback((e: React.MouseEvent, resourceId: string) => {
    if (e.button !== 0 || ganttMode !== 'resource') return
    const date = getDateFromPosition(e.clientX)
    if (!date) return

    setDragStart({ x: e.clientX, y: e.clientY, alloc: null, resourceId })
    setInteractionMode('creating')
  }, [ganttMode, getDateFromPosition])

  const handleDocumentMouseMove = useCallback((e: MouseEvent) => {
    if (!dragStart || interactionMode === 'none') return

    if (interactionMode === 'creating' && dragStart.resourceId) {
      const startDate = getDateFromPosition(dragStart.x)
      const endDate = getDateFromPosition(e.clientX)
      if (startDate && endDate) {
        const start = startDate < endDate ? startDate : endDate
        const end = startDate < endDate ? endDate : startDate
        setDragPreview({
          start: dateStr(start),
          end: dateStr(end),
          left: Math.min(dragStart.x, e.clientX),
          width: Math.abs(e.clientX - dragStart.x)
        })
      }
    } else if (interactionMode === 'dragging' && dragStart.alloc) {
      const alloc = dragStart.alloc
      const deltaX = e.clientX - dragStart.x
      const deltaCols = Math.round(deltaX / COL_WIDTH)

      if (dragMode === 'move') {
        const allocStart = parseDateUTC(alloc.allocation_start)
        const allocEnd = parseDateUTC(alloc.allocation_end)
        const duration = Math.ceil((allocEnd.getTime() - allocStart.getTime()) / 86400000)

        const newStart = new Date(allocStart)
        newStart.setUTCDate(newStart.getUTCDate() + deltaCols)
        const newEnd = new Date(newStart)
        newEnd.setUTCDate(newEnd.getUTCDate() + duration)

        const { left, width } = calcBarPos({ ...alloc, allocation_start: dateStr(newStart), allocation_end: dateStr(newEnd) })
        setDragPreview({ start: dateStr(newStart), end: dateStr(newEnd), left, width })
      } else if (dragMode === 'resize-start') {
        const newStart = new Date(parseDateUTC(alloc.allocation_start))
        newStart.setUTCDate(newStart.getUTCDate() + deltaCols)
        const allocEnd = parseDateUTC(alloc.allocation_end)
        if (newStart < allocEnd) {
          const { left, width } = calcBarPos({ ...alloc, allocation_start: dateStr(newStart) })
          setDragPreview({ start: dateStr(newStart), end: alloc.allocation_end, left, width })
        }
      } else if (dragMode === 'resize-end') {
        const newEnd = new Date(parseDateUTC(alloc.allocation_end))
        newEnd.setUTCDate(newEnd.getUTCDate() + deltaCols)
        const allocStart = parseDateUTC(alloc.allocation_start)
        if (newEnd > allocStart) {
          const { left, width } = calcBarPos({ ...alloc, allocation_end: dateStr(newEnd) })
          setDragPreview({ start: alloc.allocation_start, end: dateStr(newEnd), left, width })
        }
      }
    }
  }, [dragStart, interactionMode, dragMode, COL_WIDTH, calcBarPos, getDateFromPosition])

  const handleDocumentMouseUp = useCallback(async (e: MouseEvent) => {
    if (!dragStart) return

    if (interactionMode === 'creating' && dragStart.resourceId && dragPreview) {
      const endDate = new Date(parseDateUTC(dragPreview.end))
      endDate.setUTCDate(endDate.getUTCDate() - 1)
      setCreateForm({
        project_id: '', resource_id: dragStart.resourceId, role_type_id: '',
        allocation_start: dragPreview.start, allocation_end: dateStr(endDate),
        daily_hours: 8, billable: true, allocation_interval: 'Daily'
      })
      setShowCreateModal(true)
    } else if (interactionMode === 'dragging' && dragStart.alloc && dragPreview) {
      setSaving(true)
      try {
        const updates: any = {}
        if (dragMode === 'move' || dragMode === 'resize-start') {
          updates.allocation_start = dragPreview.start
        }
        if (dragMode === 'move' || dragMode === 'resize-end') {
          updates.allocation_end = dragPreview.end
        }
        await dataService.updateAllocation(dragStart.alloc.id, updates)
        await onRefresh()
      } catch (err: any) {
        setError(err?.message || 'Failed to update allocation')
        setTimeout(() => setError(''), 3000)
      } finally {
        setSaving(false)
      }
    } else if (interactionMode === 'split' && dragStart.alloc) {
      const date = getDateFromPosition(e.clientX)
      if (date) {
        setSplitDate(dateStr(date))
        setShowSplitModal(true)
      }
    }

    setDragStart(null)
    setDragPreview(null)
    setInteractionMode('none')
    setDragMode(null)
  }, [dragStart, interactionMode, dragMode, dragPreview, onRefresh, getDateFromPosition])

  useEffect(() => {
    if (interactionMode !== 'none') {
      document.addEventListener('mousemove', handleDocumentMouseMove)
      document.addEventListener('mouseup', handleDocumentMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleDocumentMouseMove)
        document.removeEventListener('mouseup', handleDocumentMouseUp)
      }
    }
  }, [interactionMode, handleDocumentMouseMove, handleDocumentMouseUp])

  const handleBarRightClick = useCallback((e: React.MouseEvent, alloc: ResourceAllocation) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu({ alloc, x: e.clientX, y: e.clientY })
    setSelectedAlloc(alloc)
  }, [])

  const handleSplitAllocation = async () => {
    if (!selectedAlloc || !splitDate) return
    setSaving(true)
    try {
      const split = parseDateUTC(splitDate)
      const allocStart = parseDateUTC(selectedAlloc.allocation_start)
      const allocEnd = parseDateUTC(selectedAlloc.allocation_end)

      if (split <= allocStart || split > allocEnd) {
        setError('Invalid split date')
        return
      }

      const dayBefore = new Date(split)
      dayBefore.setUTCDate(dayBefore.getUTCDate() - 1)

      const alloc1 = {
        ...selectedAlloc,
        allocation_start: selectedAlloc.allocation_start,
        allocation_end: dateStr(dayBefore)
      }
      const alloc2 = {
        ...selectedAlloc,
        allocation_start: splitDate,
        allocation_end: selectedAlloc.allocation_end
      }

      delete (alloc1 as any).id
      delete (alloc2 as any).id

      await dataService.deleteAllocation(selectedAlloc.id)
      await dataService.createAllocation(orgId, alloc1 as any)
      await dataService.createAllocation(orgId, alloc2 as any)
      await onRefresh()
      setShowSplitModal(false)
      setSelectedAlloc(null)
    } catch (err: any) {
      setError(err?.message || 'Failed to split allocation')
    } finally {
      setSaving(false)
    }
  }

  const handleCreateAllocation = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!createForm.project_id || !createForm.resource_id || !createForm.role_type_id) return
    setSaving(true)
    setError('')
    try {
      const roleType = resourceRoleTypes.find(r => r.id === createForm.role_type_id)
      await dataService.createAllocation(orgId, {
        ...createForm,
        allocation_interval: createForm.allocation_interval,
        allocation_type: 'Forecast',
        allocation_source: 'Manual',
        allocation_entry_date: new Date().toISOString().substring(0, 10),
        cost_rate_at_allocation: roleType?.cost_rate || 0,
        billing_rate_at_allocation: roleType?.billing_rate || 0,
        currency_at_allocation: roleType?.currency || 'USD'
      })
      await onRefresh()
      setShowCreateModal(false)
    } catch (err: any) {
      setError(err?.message || 'Failed to create allocation')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteAllocation = async (alloc: ResourceAllocation) => {
    if (!confirm('Delete this allocation?')) return
    setSaving(true)
    try {
      await dataService.deleteAllocation(alloc.id)
      await onRefresh()
      setContextMenu(null)
      setSelectedAlloc(null)
    } catch (err: any) {
      setError(err?.message || 'Failed to delete allocation')
    } finally {
      setSaving(false)
    }
  }

  const handleAddActionClick = (parentId: string, parentType: 'resource' | 'project') => {
    if (parentType === 'resource') {
      setCreateForm({
        project_id: '',
        resource_id: parentId,
        role_type_id: '',
        allocation_start: '',
        allocation_end: '',
        daily_hours: 8,
        billable: true,
        allocation_interval: 'Daily'
      })
    } else {
      setCreateForm({
        project_id: parentId,
        resource_id: '',
        role_type_id: '',
        allocation_start: '',
        allocation_end: '',
        daily_hours: 8,
        billable: true,
        allocation_interval: 'Daily'
      })
    }
    setShowCreateModal(true)
  }

  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(null)
      if (interactionMode === 'none') setSelectedAlloc(null)
    }
    if (contextMenu) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [contextMenu, interactionMode])

  const ganttItems = useMemo(() => {
    if (ganttMode === 'resource') {
      return resources.map(r => {
        const rAllocs = allocations.filter(a => a.resource_id === r.id)
        const projIds = Array.from(new Set(rAllocs.map(a => a.project_id)))
        const sub = projIds.map(pid => {
          const proj = projects.find(p => p.id === pid)
          return { id: pid, label: proj?.title || pid, allocations: rAllocs.filter(a => a.project_id === pid), isAction: false }
        })
        sub.push({ id: `add-project-${r.id}`, label: 'Add Project', allocations: [], isAction: true })
        return { id: r.id, label: r.title, type: 'resource' as const, allocations: [], sub }
      })
    }
    return projects.map(p => {
      const pAllocs = allocations.filter(a => a.project_id === p.id)
      const resIds = Array.from(new Set(pAllocs.map(a => a.resource_id)))
      const sub = resIds.map(rid => {
        const res = resources.find(r => r.id === rid)
        return { id: rid, label: res?.title || rid, allocations: pAllocs.filter(a => a.resource_id === rid), isAction: false }
      })
      sub.push({ id: `add-resource-${p.id}`, label: 'Add Resource', allocations: [], isAction: true })
      return { id: p.id, label: p.title, type: 'project' as const, allocations: [], sub }
    })
  }, [ganttMode, resources, projects, allocations])

  function renderBars(rowAllocs: ResourceAllocation[], topPadding = BAR_TOP_PAD, resourceId?: string) {
    const visible = rowAllocs.filter(a => {
      const s = parseDateUTC(a.allocation_start), e = parseDateUTC(a.allocation_end)
      return s <= visEnd && e >= visStart
    })
    const rows = getAllocationRows(visible)
    const rowHeight = BAR_TOP_PAD + (rows.length > 0 ? rows.length * (BAR_HEIGHT + BAR_GAP) : BAR_HEIGHT) + 4

    return { rowHeight, element: (
      <div
        ref={timelineRef}
        className={g.ganttTimeline}
        style={{ height: rowHeight, position: 'relative' }}
        onMouseDown={resourceId ? (e) => handleTimelineMouseDown(e, resourceId) : undefined}>
        {headers.map((h, i) => {
          const ds = dateStr(h.date)
          const isToday = ds === todayStr
          const isPTO = !isAutoWidth && !!resourceId && (resourceTimeOffMap[resourceId]?.has(ds) ?? false)
          const cls = isToday ? g.ganttBgCell + ' ' + g.today : h.isWeekend ? g.ganttBgCell + ' ' + g.weekend : h.isHoliday ? g.ganttBgCell + ' ' + g.holiday : isPTO ? g.ganttBgCell + ' ' + g.timeOff : ''
          if (!cls && !isToday) return null
          if (isAutoWidth) return null
          const left = i * COL_WIDTH, width = COL_WIDTH
          return <div key={i} className={cls} style={{ left, width }} />
        })}
        {rows.map((row, rowIdx) =>
          row.map(alloc => {
            const { left, width, visible } = calcBarPos(alloc)
            if (!visible) return null
            const topOff = topPadding + rowIdx * (BAR_HEIGHT + BAR_GAP)

            if (viewType === 'day') {
              if (isNonWorking(visStart)) return null
              const ds = dateStr(visStart)
              if (resourceId && (resourceTimeOffMap[resourceId]?.has(ds) ?? false)) return null
              const dailyTotal = dailyHoursMap[alloc.resource_id]?.[ds] ?? 0
              const allocHours = perAllocationHoursMap[alloc.id]?.[ds] ?? 0
              if (allocHours <= 0) return null
              const color = getBarColor(alloc.billable, dailyTotal)
              const proj = projects.find(p => p.id === alloc.project_id)
              const isSelected = selectedAlloc?.id === alloc.id
              return (
                <div key={alloc.id} className={g.ganttBar}
                  style={{
                    left: 2, right: 2, top: topOff, height: BAR_HEIGHT, backgroundColor: color, position: 'absolute',
                    cursor: 'pointer', outline: isSelected ? '2px solid #0078d4' : 'none', opacity: saving ? 0.6 : 1
                  }}
                  onMouseDown={e => handleBarMouseDown(e, alloc)}
                  onMouseMove={e => handleMouseMove(e, alloc)}
                  onMouseLeave={handleMouseLeave}
                  onContextMenu={e => handleBarRightClick(e, alloc)}>
                  {proj?.title} ({allocHours.toFixed(1)}h/d)
                </div>
              )
            }

            if (viewType === 'week' || viewType === 'month') {
              const dayMs = 86400000
              const segments: { si: number; ei: number; color: string; alloc: ResourceAllocation; dailyHours: number }[] = []
              let cur: { si: number; ei: number; color: string; alloc: ResourceAllocation; dailyHours: number } | null = null

              headers.forEach((h, i) => {
                const ds = dateStr(h.date)
                const isResourcePTO = !!resourceId && (resourceTimeOffMap[resourceId]?.has(ds) ?? false)
                if (isNonWorking(h.date) || isResourcePTO) { cur = null; return }
                const dayEnd = new Date(h.date.getTime() + dayMs)
                const as = parseDateUTC(alloc.allocation_start), ae = parseDateUTC(alloc.allocation_end)
                if (ae < h.date || as >= dayEnd) { cur = null; return }
                /*const ds = dateStr(h.date)
                const dailyTotal = dailyHoursMap[alloc.resource_id]?.[ds] ?? 0           
                if (dailyTotal <= 0) { cur = null; return }*/
                /*
                const ds = dateStr(h.date)
                const dailyHours = dailyHoursMap[alloc.resource_id]?.[ds] ?? 0           
                if (dailyHours <= 0) { cur = null; return }
                //const color = getBarColor(alloc.billable, dailyTotal)
                const color = getBarColor(alloc.billable, dailyHours)
                if (cur && cur.color === color && cur.ei === i - 1) { cur.ei = i }
                else {
                  //cur = { si: i, ei: i, color, alloc, dailyHours: dailyTotal }
                  cur = { si: i, ei: i, color, alloc, dailyHours: dailyHours }
                  segments.push(cur)
                }
                */
                // Changes made on April 1st 2026 to fix hour per day calculation on Gantt Chart
                const dailyTotal = dailyHoursMap[alloc.resource_id]?.[ds] ?? 0
                const allocHours = perAllocationHoursMap[alloc.id]?.[ds] ?? 0
                if (allocHours <= 0) { cur = null; return }
                const color = getBarColor(alloc.billable, dailyTotal)
                if (cur && cur.color === color && cur.ei === i - 1) { cur.ei = i }
                else {
                  cur = { si: i, ei: i, color, alloc, dailyHours: allocHours }
                  segments.push(cur)
                }
              })

              return segments.map((seg, idx) => {
                const segLeft = seg.si * COL_WIDTH, segWidth = (seg.ei - seg.si + 1) * COL_WIDTH
                const rawProj = projects.find(p => p.id === seg.alloc.project_id)?.title || ''
                const projName = segWidth > 145 ? truncate(rawProj, 26) : segWidth > 120 ? truncate(rawProj, 18) : truncate(rawProj, 10)
                const label = segWidth >= 64 ? `${projName} ${Number(seg.dailyHours).toFixed(1)}h/d` : segWidth >= 28 ? `${Number(seg.dailyHours).toFixed(1)}h` : ''
                const isSelected = selectedAlloc?.id === seg.alloc.id
                const cursorStyle = dragMode === 'resize-start' ? 'ew-resize' : dragMode === 'resize-end' ? 'ew-resize' : 'grab'
                return (
                  <div key={`${alloc.id}-seg-${idx}`} className={g.ganttBar}
                    style={{
                      left: segLeft, width: segWidth, top: topOff, height: BAR_HEIGHT, backgroundColor: seg.color, position: 'absolute',
                      cursor: cursorStyle, outline: isSelected ? '2px solid #0078d4' : 'none', opacity: saving ? 0.6 : 1
                    }}
                    onMouseDown={e => handleBarMouseDown(e, seg.alloc)}
                    onMouseMove={e => handleMouseMove(e, seg.alloc)}
                    onMouseLeave={handleMouseLeave}
                    onContextMenu={e => handleBarRightClick(e, seg.alloc)}>
                    {label}
                  </div>
                )
              })
            }

            if (viewType === 'quarter' || viewType === 'year') {
              const rangeStart = new Date(Math.max(parseDateUTC(alloc.allocation_start).getTime(), visStart.getTime()))
              const rangeEnd = new Date(Math.min(parseDateUTC(alloc.allocation_end).getTime(), visEnd.getTime()))

              const totalHours = allocationDistributionService.getHoursForDateRange(
                alloc.allocation_start,
                alloc.allocation_end,
                alloc.daily_hours,
                alloc.allocation_interval,
                rangeStart.toISOString().substring(0, 10),
                rangeEnd.toISOString().substring(0, 10),
                holidays
              )

              const color = alloc.billable ? '#2D55A5' : COLORS.nonBillable
              const isSelected = selectedAlloc?.id === alloc.id
              return (
                <div key={alloc.id} className={g.ganttBar}
                  style={{
                    left, width, top: topOff, height: BAR_HEIGHT, backgroundColor: color, position: 'absolute',
                    cursor: 'pointer', outline: isSelected ? '2px solid #0078d4' : 'none', opacity: saving ? 0.6 : 1
                  }}
                  onMouseDown={e => handleBarMouseDown(e, alloc)}
                  onMouseMove={e => handleMouseMove(e, alloc)}
                  onMouseLeave={handleMouseLeave}
                  onContextMenu={e => handleBarRightClick(e, alloc)}>
                  {width > 32 ? `${Number(totalHours).toFixed(1)}h` : ''}
                </div>
              )
            }

            return null
          })
        )}
        {dragPreview && dragStart?.resourceId === resourceId && interactionMode === 'creating' && (
          <div
            style={{
              position: 'absolute',
              left: dragPreview.left,
              width: dragPreview.width,
              top: BAR_TOP_PAD,
              height: BAR_HEIGHT,
              backgroundColor: '#0078d4',
              opacity: 0.4,
              pointerEvents: 'none',
              border: '2px dashed #0078d4'
            }}
          />
        )}
        {dragPreview && dragStart?.alloc && interactionMode === 'dragging' && (
          <div
            style={{
              position: 'absolute',
              left: dragPreview.left,
              width: dragPreview.width,
              top: BAR_TOP_PAD,
              height: BAR_HEIGHT,
              backgroundColor: '#0078d4',
              opacity: 0.5,
              pointerEvents: 'none',
              border: '2px solid #0078d4',
              borderRadius: 2
            }}
          />
        )}
      </div>
    ) }
  }

  const gridTemplateColumns = isAutoWidth
    ? `repeat(${totalCols}, 1fr)`
    : `repeat(${totalCols}, ${COL_WIDTH}px)`

  return (
    <div className={g.ganttContainer}>
      <div className={g.ganttControls}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className={g.ganttModeBtns}>
            <button className={`${g.modeBtn} ${ganttMode === 'resource' ? g.active : ''}`} onClick={() => setGanttMode('resource')}>
              Resource View
            </button>
            <button className={`${g.modeBtn} ${ganttMode === 'project' ? g.active : ''}`} onClick={() => setGanttMode('project')}>
              Project View
            </button>
          </div>
          <div className={g.viewBtns}>
            {(['day', 'week', 'month', 'quarter', 'year'] as ViewType[]).map(v => (
              <button key={v} className={`${g.viewBtn} ${viewType === v ? g.active : ''}`} onClick={() => setViewType(v)}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div className={g.navBtns}>
          <button className={g.navArrow} onClick={() => navigate(-1)}><ArrowLeft size={13} /></button>
          <span className={g.periodLabel}>{dateCalculationService.formatDateRange(visStart, visEnd, viewType)}</span>
          <button className={g.navArrow} onClick={() => navigate(1)}><ArrowRight size={13} /></button>
          <button className={g.todayBtn} onClick={goToday}>Today</button>
        </div>
      </div>

      <div className={g.ganttScrollWrapper} ref={scrollRef}>
        <div className={g.ganttGrid}>
          <div className={g.ganttLabelHeader}>
            {ganttMode === 'resource' ? 'Resource / Project' : 'Project / Resource'}
          </div>
          <div className={g.ganttTimelineHeader} style={{ display: 'grid', gridTemplateColumns, minWidth: isAutoWidth ? undefined : `${totalCols * COL_WIDTH}px` }}>
  {headers.map((h, i) => (
    <div key={i} className={`${g.ganttColHeader}${h.isWeekend ? ' ' + g.weekend : ''}${h.isHoliday ? ' ' + g.holiday : ''}`}>
      {h.label}
    </div>
  ))}
</div>


          {ganttItems.length === 0 && (
            <>
              <div className={g.ganttLabel} style={{ minHeight: 60 }} />
              <div className={g.ganttTimeline} style={{ minHeight: 60 }}>
                <div className={g.emptyState}>No data to display</div>
              </div>
            </>
          )}

          {ganttItems.map(item => {
            if (item.type === 'resource' && item.sub.length === 0) {
              // Resource with no allocations - single row
              const { rowHeight, element } = renderBars([], BAR_TOP_PAD, item.id)
              return (
                <React.Fragment key={item.id}>
                  <div className={`${g.ganttLabel} ${g.projectLabel}`}>{item.label}</div>
                  {element}
                </React.Fragment>
              )
            }
            // Parent item (resource in resource view, project in project view)
            return (
              <React.Fragment key={item.id}>
                <div className={`${g.ganttLabel} ${g.projectLabel}`}>{truncate(item.label, 28)}</div>
                <div className={`${g.ganttTimeline} ${g.projectTimeline}`} style={{ minHeight: 32 }}>
                  {headers.map((h, i) => {
                    const ds = dateStr(h.date)
                    const isPTO = !isAutoWidth && item.type === 'resource' && (resourceTimeOffMap[item.id]?.has(ds) ?? false)
                    if (!isAutoWidth && (h.isWeekend || h.isHoliday || isPTO)) {
                      const cls = h.isWeekend ? g.weekend : h.isHoliday ? g.holiday : g.timeOff
                      return <div key={i} className={`${g.ganttBgCell} ${cls}`} style={{ left: i * COL_WIDTH, width: COL_WIDTH }} />
                    }
                    return null
                  })}
                </div>
                {item.sub.map(sub => {
                  if (sub.isAction) {
                    return (
                      <React.Fragment key={sub.id}>
                        <div className={g.actionButtonRow}>
                          <button
                            className={g.actionButton}
                            onClick={() => handleAddActionClick(item.id, item.type)}
                          >
                            <span style={{ fontSize: '14px', lineHeight: 1 }}>+</span>
                            {sub.label}
                          </button>
                        </div>
                        <div className={g.actionButtonTimeline}>
                          {headers.map((h, i) => {
                            const ds = dateStr(h.date)
                            const isPTO = !isAutoWidth && item.type === 'resource' && (resourceTimeOffMap[item.id]?.has(ds) ?? false)
                            if (!isAutoWidth && (h.isWeekend || h.isHoliday || isPTO)) {
                              const cls = h.isWeekend ? g.weekend : h.isHoliday ? g.holiday : g.timeOff
                              return <div key={i} className={`${g.ganttBgCell} ${cls}`} style={{ left: i * COL_WIDTH, width: COL_WIDTH }} />
                            }
                            return null
                          })}
                        </div>
                      </React.Fragment>
                    )
                  }
                  //const { rowHeight, element } = renderBars(sub.allocations)
                const { rowHeight, element } = renderBars(
                  sub.allocations,
                  BAR_TOP_PAD,
                  item.type === 'resource' ? item.id : sub.id
                )
                  return (
                    <React.Fragment key={sub.id}>
                      <div className={`${g.ganttLabel} ${g.subLabel}`} style={{ minHeight: rowHeight }}>{sub.label}</div>
                      {element}
                    </React.Fragment>
                  )
                })}
              </React.Fragment>
            )
          })}
        </div>
      </div>

      <div className={g.legend}>
        <span className={g.legendItem}><span className={g.legendDot} style={{ background: COLORS.overbooked }} />Overbooked (&gt;8h)</span>
        <span className={g.legendItem}><span className={g.legendDot} style={{ background: COLORS.full }} />Full (8h)</span>
        <span className={g.legendItem}><span className={g.legendDot} style={{ background: COLORS.partial }} />Partial (3-8h)</span>
        <span className={g.legendItem}><span className={g.legendDot} style={{ background: COLORS.low }} />Low (&lt;3h)</span>
        <span className={g.legendItem}><span className={g.legendDot} style={{ background: COLORS.nonBillable }} />Non-Billable</span>
        <span className={g.legendItem}><span className={g.legendDot} style={{ background: 'rgba(0,180,160,0.25)', border: '1px dashed rgba(0,180,160,0.6)' }} />Time Off (approved)</span>
      </div>

      {error && (
        <div style={{
          position: 'fixed', top: 20, right: 20, background: '#d13438', color: 'white',
          padding: '12px 16px', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10000
        }}>
          {error}
        </div>
      )}

      {tooltip && (() => {
        const { alloc, x, y } = tooltip
        const proj = projects.find(p => p.id === alloc.project_id)
        const res = resources.find(r => r.id === alloc.resource_id)
        const role = resourceRoleTypes.find(rt => rt.id === alloc.role_type_id)
        const cust = proj ? customers.find(c => c.id === proj.customer_id) : null
        return (
          <div className={g.tooltip} style={{ left: x, top: y }}>
            <div className={g.tooltipTitle}>Allocation Details</div>
            <div className={g.tooltipRow}><span className={g.tooltipLabel}>Resource</span><span className={g.tooltipValue}>{res?.title || '—'}</span></div>
            <div className={g.tooltipRow}><span className={g.tooltipLabel}>Project</span><span className={g.tooltipValue}>{proj?.title || '—'}</span></div>
            <div className={g.tooltipRow}><span className={g.tooltipLabel}>Customer</span><span className={g.tooltipValue}>{cust?.title || '—'}</span></div>
            <div className={g.tooltipRow}><span className={g.tooltipLabel}>Role</span><span className={g.tooltipValue}>{role?.title || '—'}</span></div>
            <div className={g.tooltipRow}>
              <span className={g.tooltipLabel}>Period</span>
              <span className={g.tooltipValue}>
                {parseDateUTC(alloc.allocation_start).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })}
                {' – '}
                {parseDateUTC(alloc.allocation_end).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
            <div className={g.tooltipRow}>
              <span className={g.tooltipLabel}>
                {alloc.allocation_interval === 'Daily' ? 'Hours/Day' : alloc.allocation_interval === 'Weekly' ? 'Hours/Week' : 'Hours/Month'}
              </span>
              <span className={g.tooltipValue}>{alloc.daily_hours}h</span>
            </div>
            {alloc.allocation_interval !== 'Daily' && (() => {
  const dailyHours = perAllocationHoursMap[alloc.id] || {}
  const values = Object.values(dailyHours).filter(h => h > 0)
  const avgDaily = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0

  return avgDaily > 0 ? (
    <div className={g.tooltipRow}>
      <span className={g.tooltipLabel}>Avg Daily</span>
      <span className={g.tooltipValue} style={{ fontSize: '11px', opacity: 0.8 }}>
        ~{avgDaily.toFixed(1)}h/day
      </span>
    </div>
  ) : null
})()}

            <div className={g.tooltipRow}>
              <span className={g.tooltipLabel}>Interval</span>
              <span className={g.tooltipBadge} style={{ background: '#e6f4ea', color: '#107c10' }}>
                {alloc.allocation_interval}
              </span>
            </div>
            <div className={g.tooltipRow}>
              <span className={g.tooltipLabel}>Billable</span>
              <span className={g.tooltipBadge} style={{ background: alloc.billable ? '#e6f4ea' : '#f3f2f1', color: alloc.billable ? '#107c10' : '#605e5c' }}>
                {alloc.billable ? 'Yes' : 'No'}
              </span>
            </div>
            <div className={g.tooltipRow}><span className={g.tooltipLabel}>Billing Rate</span><span className={g.tooltipValue}>{currencyService.formatCurrency(alloc.billing_rate_at_allocation, alloc.currency_at_allocation)}/hr</span></div>
            <div className={g.tooltipRow}><span className={g.tooltipLabel}>Cost Rate</span><span className={g.tooltipValue}>{currencyService.formatCurrency(alloc.cost_rate_at_allocation, alloc.currency_at_allocation)}/hr</span></div>
          </div>
        )
      })()}

      {contextMenu && (() => {
        const { alloc, x, y } = contextMenu
        return (
          <div style={{
            position: 'fixed', left: x, top: y, background: 'white', border: '1px solid #ddd',
            borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10000, minWidth: 180
          }}>
            <div style={{ padding: '4px 0' }}>
              <button
                onClick={() => { setSelectedAlloc(alloc); setShowSplitModal(true); setContextMenu(null) }}
                style={{
                  display: 'block', width: '100%', padding: '8px 16px', border: 'none', background: 'transparent',
                  textAlign: 'left', cursor: 'pointer', fontSize: 14
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f3f2f1'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                Split Allocation
              </button>
              <button
                onClick={() => handleDeleteAllocation(alloc)}
                style={{
                  display: 'block', width: '100%', padding: '8px 16px', border: 'none', background: 'transparent',
                  textAlign: 'left', cursor: 'pointer', fontSize: 14, color: '#d13438'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f3f2f1'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                Delete Allocation
              </button>
            </div>
          </div>
        )
      })()}

      <Modal
        open={showCreateModal}
        title="Create Allocation"
        onClose={() => setShowCreateModal(false)}
        maxWidth={500}
        footer={
          <>
            <button onClick={() => setShowCreateModal(false)} style={{
              padding: '8px 16px', border: '1px solid #8a8886', background: 'white', borderRadius: 4, cursor: 'pointer'
            }}>
              Cancel
            </button>
            <button form="createAllocForm" type="submit" disabled={saving} style={{
              padding: '8px 16px', border: 'none', background: '#0078d4', color: 'white', borderRadius: 4,
              cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, marginLeft: 8
            }}>
              {saving ? 'Creating...' : 'Create'}
            </button>
          </>
        }
      >
        <form id="createAllocForm" onSubmit={handleCreateAllocation} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {error && <div style={{ padding: 12, background: '#fde7e9', color: '#d13438', borderRadius: 4, fontSize: 14 }}>{error}</div>}
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 600 }}>Project *</label>
            <select
              value={createForm.project_id}
              onChange={e => setCreateForm(f => ({ ...f, project_id: e.target.value }))}
              required
              disabled={ganttMode === 'project'}
              style={{ width: '100%', padding: '8px', border: '1px solid #8a8886', borderRadius: 4, fontSize: 14, background: ganttMode === 'project' ? '#f3f2f1' : 'white' }}
            >
              <option value="">Select project...</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 600 }}>Resource *</label>
            <select
              value={createForm.resource_id}
              onChange={e => setCreateForm(f => ({ ...f, resource_id: e.target.value }))}
              required
              disabled={ganttMode === 'resource'}
              style={{ width: '100%', padding: '8px', border: '1px solid #8a8886', borderRadius: 4, fontSize: 14, background: ganttMode === 'resource' ? '#f3f2f1' : 'white' }}
            >
              <option value="">Select resource...</option>
              {resources.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 600 }}>Role Type *</label>
            <select
              value={createForm.role_type_id}
              onChange={e => setCreateForm(f => ({ ...f, role_type_id: e.target.value }))}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #8a8886', borderRadius: 4, fontSize: 14 }}
            >
              <option value="">Select role...</option>
              {resourceRoleTypes.filter(r => r.active).map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 600 }}>Start Date *</label>
              <input
                type="date"
                value={createForm.allocation_start}
                onChange={e => setCreateForm(f => ({ ...f, allocation_start: e.target.value }))}
                required
                style={{ width: '100%', padding: '8px', border: '1px solid #8a8886', borderRadius: 4, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 600 }}>End Date *</label>
              <input
                type="date"
                value={createForm.allocation_end}
                onChange={e => setCreateForm(f => ({ ...f, allocation_end: e.target.value }))}
                required
                style={{ width: '100%', padding: '8px', border: '1px solid #8a8886', borderRadius: 4, fontSize: 14 }}
              />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 600 }}>Allocation Interval *</label>
            <select
              value={createForm.allocation_interval}
              onChange={e => setCreateForm(f => ({ ...f, allocation_interval: e.target.value as AllocationInterval }))}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #8a8886', borderRadius: 4, fontSize: 14 }}
            >
              <option value="Daily">Daily</option>
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
            </select>
            <div style={{ fontSize: 12, color: '#605e5c', marginTop: 4 }}>
              {createForm.allocation_interval === 'Daily' && 'Hours will be allocated per working day'}
              {createForm.allocation_interval === 'Weekly' && 'Hours will be distributed evenly across working days in each week'}
              {createForm.allocation_interval === 'Monthly' && 'Hours will be distributed evenly across working days in each month'}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 600 }}>
                {createForm.allocation_interval === 'Daily' && 'Hours per Day *'}
                {createForm.allocation_interval === 'Weekly' && 'Hours per Week *'}
                {createForm.allocation_interval === 'Monthly' && 'Hours per Month *'}
              </label>
              <input
                type="number"
                min="0.5"
                max={createForm.allocation_interval === 'Daily' ? 24 : createForm.allocation_interval === 'Weekly' ? 168 : 744}
                step="0.5"
                value={createForm.daily_hours}
                onChange={e => setCreateForm(f => ({ ...f, daily_hours: parseFloat(e.target.value) || 8 }))}
                required
                style={{ width: '100%', padding: '8px', border: '1px solid #8a8886', borderRadius: 4, fontSize: 14 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={createForm.billable}
                  onChange={e => setCreateForm(f => ({ ...f, billable: e.target.checked }))}
                />
                <span style={{ fontSize: 14, fontWeight: 600 }}>Billable</span>
              </label>
            </div>
          </div>
        </form>
      </Modal>

      <Modal
        open={showSplitModal}
        title="Split Allocation"
        onClose={() => setShowSplitModal(false)}
        maxWidth={400}
        footer={
          <>
            <button onClick={() => setShowSplitModal(false)} style={{
              padding: '8px 16px', border: '1px solid #8a8886', background: 'white', borderRadius: 4, cursor: 'pointer'
            }}>
              Cancel
            </button>
            <button onClick={handleSplitAllocation} disabled={saving} style={{
              padding: '8px 16px', border: 'none', background: '#0078d4', color: 'white', borderRadius: 4,
              cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, marginLeft: 8
            }}>
              {saving ? 'Splitting...' : 'Split'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && <div style={{ padding: 12, background: '#fde7e9', color: '#d13438', borderRadius: 4, fontSize: 14 }}>{error}</div>}
          {selectedAlloc && (
            <>
              <div style={{ padding: 12, background: '#f3f2f1', borderRadius: 4, fontSize: 14 }}>
                <div><strong>Current Period:</strong></div>
                <div>{selectedAlloc.allocation_start} to {selectedAlloc.allocation_end}</div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 600 }}>Split Date *</label>
                <input
                  type="date"
                  value={splitDate}
                  onChange={e => setSplitDate(e.target.value)}
                  min={selectedAlloc.allocation_start}
                  max={selectedAlloc.allocation_end}
                  required
                  style={{ width: '100%', padding: '8px', border: '1px solid #8a8886', borderRadius: 4, fontSize: 14 }}
                />
                <div style={{ marginTop: 8, fontSize: 12, color: '#605e5c' }}>
                  This will create two allocations with the same properties
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  )
}
